import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  getCookieConsent,
  setCookieConsent,
  getDefaultPreferences,
  type CookiePreferences,
} from "@/utils/cookieConsent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getDefaultPreferences());

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setShowBanner(true);
    } else {
      setPreferences(consent);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setCookieConsent(allAccepted);
    setShowBanner(false);
    window.location.reload(); // Reload to initialize analytics
  };

  const handleRejectAll = () => {
    const onlyEssential = getDefaultPreferences();
    setCookieConsent(onlyEssential);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    setCookieConsent(preferences);
    setShowSettings(false);
    setShowBanner(false);
    window.location.reload(); // Reload to apply preferences
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-slide-up">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold mb-2">Respetamos tu Privacidad</h3>
              <p className="text-sm text-muted-foreground">
                Utilizamos cookies para mejorar tu experiencia. Las cookies esenciales son necesarias para el funcionamiento del sitio.
                Consulta nuestra{" "}
                <a 
                  href="/privacidad" 
                  className="text-primary hover:underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Leer nuestra Política de Privacidad"
                >
                  Política de Privacidad
                </a>{" "}
                para más detalles.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenSettings}
                className="whitespace-nowrap"
              >
                Configurar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="whitespace-nowrap"
              >
                Rechazar Todo
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="whitespace-nowrap"
              >
                Aceptar Todo
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preferencias de Cookies</DialogTitle>
            <DialogDescription>
              Gestiona tus preferencias de cookies. Las cookies esenciales no se pueden desactivar ya que son necesarias para el funcionamiento del sitio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            <div className="flex items-start justify-between gap-4 pb-4 border-b">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Cookies Esenciales</h4>
                <p className="text-sm text-muted-foreground">
                  Necesarias para el funcionamiento básico del sitio web. Incluyen gestión de sesiones, 
                  seguridad y funcionalidades esenciales. No se pueden desactivar.
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            <div className="flex items-start justify-between gap-4 pb-4 border-b">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Cookies Analíticas</h4>
                <p className="text-sm text-muted-foreground">
                  Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web mediante la 
                  recopilación de información de forma anónima. Esto nos permite mejorar la experiencia del usuario.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Servicios: Analytics interno, métricas de rendimiento
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4 pb-4">
              <div className="flex-1">
                <h4 className="font-semibold mb-1">Cookies de Marketing</h4>
                <p className="text-sm text-muted-foreground">
                  Utilizadas para rastrear visitantes en sitios web y mostrar anuncios relevantes. 
                  También se usan para seguimiento de conversiones de afiliados y personalización de contenido.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Servicios: Seguimiento de afiliados, personalización de contenido
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePreferences}>
              Guardar Preferencias
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
